import { useAuth } from '../context/AuthContext';
import { supabase } from '../utils/supabase';
import { useToast } from '../context/ToastContext';
import { RubricCategory, RubricCriterion } from './useRubric';

// This hook provides only the operations without fetching rubric data
// Use this when you already have rubric data passed as props
export function useRubricOperations(onRefresh?: () => Promise<void>) {
  const { user } = useAuth();
  const { showToast } = useToast();

  // Update category
  const updateCategory = async (
    categoryId: string,
    updates: Partial<RubricCategory>
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('rubric_categories')
        .update({
          name: updates.name,
          description: updates.description,
          weight: updates.weight,
          display_order: updates.display_order
        })
        .eq('id', categoryId);

      if (error) throw error;

      if (onRefresh) await onRefresh();
      showToast('Category updated successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to update category:', error);
      showToast('Failed to update category', 'error');
      return false;
    }
  };

  // Delete category
  const deleteCategory = async (categoryId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Check for evidence links to criteria in this category
      const { data: criteria } = await supabase
        .from('rubric_criteria')
        .select('id')
        .eq('category_id', categoryId);

      if (criteria && criteria.length > 0) {
        const criteriaIds = criteria.map(c => c.id);
        const { data: links } = await supabase
          .from('content_links')
          .select('id')
          .eq('target_type', 'rubric_criterion')
          .in('target_id', criteriaIds);

        if (links && links.length > 0) {
          const confirmed = window.confirm(
            `This category has ${links.length} evidence links. Deleting it will remove these links. Continue?`
          );
          if (!confirmed) return false;
        }
      }

      const { error } = await supabase
        .from('rubric_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      if (onRefresh) await onRefresh();
      showToast('Category deleted successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to delete category:', error);
      showToast('Failed to delete category', 'error');
      return false;
    }
  };

  // Add category
  const addCategory = async (
    rubricId: string,
    category: Omit<RubricCategory, 'id' | 'rubric_id' | 'created_at'>
  ): Promise<RubricCategory | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('rubric_categories')
        .insert({
          rubric_id: rubricId,
          name: category.name,
          description: category.description,
          weight: category.weight,
          display_order: category.display_order
        })
        .select()
        .single();

      if (error) throw error;

      if (onRefresh) await onRefresh();
      showToast('Category added successfully', 'success');
      return data;
    } catch (error) {
      console.error('Failed to add category:', error);
      showToast('Failed to add category', 'error');
      return null;
    }
  };

  // Update criterion
  const updateCriterion = async (
    criterionId: string,
    updates: Partial<RubricCriterion>
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('rubric_criteria')
        .update({
          text: updates.text,
          description: updates.description,
          display_order: updates.display_order
        })
        .eq('id', criterionId);

      if (error) throw error;

      if (onRefresh) await onRefresh();
      showToast('Criterion updated successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to update criterion:', error);
      showToast('Failed to update criterion', 'error');
      return false;
    }
  };

  // Delete criterion
  const deleteCriterion = async (criterionId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Check for evidence links
      const { data: links } = await supabase
        .from('content_links')
        .select('id')
        .eq('target_type', 'rubric_criterion')
        .eq('target_id', criterionId);

      if (links && links.length > 0) {
        const confirmed = window.confirm(
          `This criterion has ${links.length} evidence links. Deleting it will remove these links. Continue?`
        );
        if (!confirmed) return false;
      }

      const { error } = await supabase
        .from('rubric_criteria')
        .delete()
        .eq('id', criterionId);

      if (error) throw error;

      if (onRefresh) await onRefresh();
      showToast('Criterion deleted successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to delete criterion:', error);
      showToast('Failed to delete criterion', 'error');
      return false;
    }
  };

  // Add criterion
  const addCriterion = async (
    categoryId: string,
    criterion: Omit<RubricCriterion, 'id' | 'category_id' | 'created_at'>
  ): Promise<RubricCriterion | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('rubric_criteria')
        .insert({
          category_id: categoryId,
          text: criterion.text,
          description: criterion.description,
          display_order: criterion.display_order
        })
        .select()
        .single();

      if (error) throw error;

      if (onRefresh) await onRefresh();
      showToast('Criterion added successfully', 'success');
      return data;
    } catch (error) {
      console.error('Failed to add criterion:', error);
      showToast('Failed to add criterion', 'error');
      return null;
    }
  };

  // Reorder categories
  const reorderCategories = async (
    rubricId: string,
    categoryIds: string[]
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      // Update display_order for each category
      const updates = categoryIds.map((id, index) => ({
        id,
        display_order: index
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('rubric_categories')
          .update({ display_order: update.display_order })
          .eq('id', update.id)
          .eq('rubric_id', rubricId);

        if (error) throw error;
      }

      if (onRefresh) await onRefresh();
      return true;
    } catch (error) {
      console.error('Failed to reorder categories:', error);
      showToast('Failed to reorder categories', 'error');
      return false;
    }
  };

  return {
    updateCategory,
    deleteCategory,
    addCategory,
    updateCriterion,
    deleteCriterion,
    addCriterion,
    reorderCategories
  };
}